import { observer } from "mobx-react-lite";
import { FunctionComponent, useContext, useEffect, useRef } from "react";

import {
  Heading,
  IContextualMenuItem,
  IPivotItemProps,
  Pivot,
  PivotItem,
  Stack
} from "@bps/fluent-ui";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { AddContextualMenuButton } from "@ui-components/AddContextualMenuButton.tsx";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";

import {
  InvestigationsSectionKeys,
  InvestigationsSectionText
} from "../../Investigations.Types.ts";
import { InvestigationSidePanelHelper } from "./InvestigationSidePanelHelper.ts";
import { InvestigationSidePanelContext } from "./InvestigationsSidePanelContext.ts";
import { InvestigationsSidePanelDialogs } from "./InvestigationsSidePanelDialogs.tsx";
import { ReportsList } from "./ReportsList.tsx";
import { RequestsList } from "./RequestsList.tsx";

export interface InvestigationsSidePanelProps {
  clinicalRecord: ClinicalRecord;
}

const SidePanel: FunctionComponent<InvestigationsSidePanelProps> = observer(
  ({ clinicalRecord }) => {
    const {
      handleViewClick,
      pageSelected,
      onCreateReportClick,
      onCreateRequestClick
    } = useContext(InvestigationSidePanelContext);

    const { isViewOnlyOrDischarged } = usePatientRecordScreenContext();
    useEffect(() => {
      clinicalRecord.loadPatientUserTasks();
    }, [clinicalRecord]);

    const renderItemLink = (
      onLinkClick: () => void,
      link?: IPivotItemProps,
      defaultRenderer?: (link?: IPivotItemProps) => JSX.Element | null
    ) => {
      if (defaultRenderer)
        return <>{defaultRenderer({ ...link, itemIcon: undefined })}</>;
      else return null;
    };

    const investigationMenuItems: IContextualMenuItem[] = [
      {
        name: "New report",
        key: "new report",
        onClick: () => onCreateReportClick()
      },
      {
        name: "New request",
        key: "new request",
        onClick: () => onCreateRequestClick()
      }
    ];

    const INVESTIGATION_AUTOMATION_ID = "investigation -contextual-menu-btn";

    return (
      <>
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <Heading variant="section-heading-light" labelPaddings={true}>
            Investigations
          </Heading>
          {!isViewOnlyOrDischarged && (
            <AddContextualMenuButton
              menuItems={investigationMenuItems}
              automationId={INVESTIGATION_AUTOMATION_ID}
            />
          )}
        </Stack>

        <Pivot onLinkClick={handleViewClick} selectedKey={pageSelected}>
          <PivotItem
            headerText={InvestigationsSectionText.Reports}
            itemKey={InvestigationsSectionKeys.Reports}
            onRenderItemLink={(link, defaultRenderer) =>
              renderItemLink(onCreateReportClick, link, defaultRenderer)
            }
          />
          <PivotItem
            headerText={InvestigationsSectionText.Requests}
            itemKey={InvestigationsSectionKeys.Requests}
            onRenderItemLink={(link, defaultRenderer) =>
              renderItemLink(onCreateRequestClick, link, defaultRenderer)
            }
          />
        </Pivot>

        {pageSelected === InvestigationsSectionKeys.Reports && <ReportsList />}
        {pageSelected === InvestigationsSectionKeys.Requests && (
          <RequestsList />
        )}

        <InvestigationsSidePanelDialogs />
      </>
    );
  }
);

const SidePanelWrapper: React.FC<InvestigationsSidePanelProps> = ({
  ...props
}) => {
  const { isViewOnlyOrDischarged } = usePatientRecordScreenContext();
  const helper = useRef(
    new InvestigationSidePanelHelper(
      props.clinicalRecord,
      useStores(),
      isViewOnlyOrDischarged
    )
  );

  return (
    <InvestigationSidePanelContext.Provider value={helper.current}>
      <SidePanel {...props} />
    </InvestigationSidePanelContext.Provider>
  );
};

export const InvestigationsSidePanel = withFetch(
  x => [
    x.correspondence.ref.correspondenceTypes.load(),
    x.correspondence.ref.correspondenceDirections.load(),
    x.correspondence.ref.correspondenceStatuses.load(),
    x.inbox.ref.outcomes.load()
  ],
  SidePanelWrapper
);
